import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Trash2, ChevronLeft, ChevronRight } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import integrationsService from '../../services/integrationsService';
import { apiErrorMessage } from '../../utils/apiError';
import Button from '../shared/Button';
import { SubTabs } from '../shared/Tabs';
import Spinner from '../shared/Spinner';
import ConfirmModal from '../shared/ConfirmModal';
import ImplementationBoard from '../shared/ImplementationBoard';
import { ImplementationStatusSelect, ImplementationVersionSelect } from '../shared/ImplementationControls';
import IntegrationLogsTab from '../IntegrationLogsTab';
import PipelineConversionsTab from './PipelineConversionsTab';
import '../IntegrationForm.css';
import '../shared/ImplementationWizard.css';
import PipelinesHeader from './PipelinesHeader';
import ProductSupportTab from '../support/ProductSupportTab';

// ONE HOTEL'S PIPELINE — a single implementation (a hotel + a pipeline template), opened from Pipelines › Hotels.
//
// The header says which template and which version it runs; the version is moved here, one property at a time (V672) —
// the next message for this hotel routes with the version chosen. The Pipeline tab is the shared ImplementationBoard
// (credentials red until set); the Logs tab is the integration log narrowed to this template at this hotel (superadmin
// only, as the log is on the server).
//
// The shape of the pipeline is changed on the template (the link in the header), never here.
//
// UNDER THE PIPELINES TAB BAR, Hotels lit: this page is a drill-down of that tab, so the bar the user clicked from stays
// put and any tab on it goes back to the Pipelines page (PipelinesHeader). This hotel's own Pipeline | Logs | Support are the
// second level — Support lists the tickets about this one pipeline (product Pipelines, id the implementation).
const TABS = ['pipeline', 'conversions', 'logs', 'support'];

export default function PipelineImplementationPage() {
  const { templateId, implementationId } = useParams();
  const navigate = useNavigate();
  const { isSuperAdmin } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = TABS.includes(searchParams.get('tab')) ? searchParams.get('tab') : 'pipeline';
  const setTab = (t) => setSearchParams(t === 'pipeline' ? {} : { tab: t }, { replace: true });

  const [template, setTemplate] = useState(null);
  const [impl, setImpl] = useState(null);           // the list row: status, version, filled of slots
  const [siblings, setSiblings] = useState([]);     // this template's hotels, ordered — to wheel prev/next between them
  const [versions, setVersions] = useState({ versions: [], liveVersionId: null });
  const [graph, setGraph] = useState(null);
  const [error, setError] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [busy, setBusy] = useState(null);           // 'status' | 'version' | 'remove'
  const [confirm, setConfirm] = useState(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      const [t, list, v, graphs] = await Promise.all([
        integrationsService.getIntegrationById(templateId),
        integrationsService.getImplementations(templateId),
        integrationsService.listVersions(templateId).catch(() => ({ versions: [], liveVersionId: null })),
        integrationsService.getBoardGraphs([templateId]),
      ]);
      const impls = (list.implementations || []).slice()
        .sort((a, b) => String(a.hotelname || a.name || '').localeCompare(String(b.hotelname || b.name || '')));
      const row = impls.find(x => String(x.id) === String(implementationId));
      if (!row) { setNotFound(true); return; }
      setSiblings(impls.map(x => ({ id: x.id, hotelname: x.hotelname || x.name })));
      setTemplate(t.integration || null);
      setImpl(row);
      setVersions(v || { versions: [], liveVersionId: null });
      setGraph(graphs[templateId] || null);
    } catch (e) {
      if (e.response && e.response.status === 404) setNotFound(true);
      else setError(apiErrorMessage(e, 'This pipeline could not be opened.'));
    }
  }, [templateId, implementationId]);
  useEffect(() => { load(); }, [load]);

  const changeStatus = async (status) => {
    if (status === impl.status) return;
    setBusy('status'); setError(null);
    try {
      await integrationsService.setImplementationStatus(templateId, impl.id, status);
      setImpl(x => ({ ...x, status }));
    } catch (e) {
      setError(apiErrorMessage(e, 'The status could not be changed.'));
    } finally { setBusy(null); }
  };

  const publishedVersions = versions.versions || [];
  const changeVersion = (versionId) => {
    const target = publishedVersions.find(v => String(v.id) === String(versionId));
    if (!target || String(versionId) === String(impl.version_id)) return;
    setConfirm({
      title: `Move ${impl.hotelname || impl.name} to v${target.version}`,
      message: `${impl.hotelname || 'This hotel'} routes with v${target.version} of ${template ? template.name : 'the template'} from its next message`
        + `${impl.version ? ` (it is on v${impl.version} now)` : ''}. No other hotel changes.`,
      confirmLabel: `Move to v${target.version}`,
      onConfirm: async () => {
        setConfirm(null);
        setBusy('version'); setError(null);
        try {
          const res = await integrationsService.setImplementationVersion(templateId, impl.id, target.id);
          const moved = (res && res.implementation) || {};
          setImpl(x => ({ ...x, version_id: moved.version_id ?? target.id, version: moved.version ?? target.version,
            pinned_version: target.version, version_pinned: 1 }));
        } catch (e) {
          setError(apiErrorMessage(e, 'The hotel could not be moved to that version.'));
        } finally { setBusy(null); }
      },
    });
  };

  const remove = () => setConfirm({
    title: 'Remove this pipeline from the hotel',
    message: `Stop ${impl.hotelname || 'this hotel'} running ${template ? template.name : 'this template'}? `
      + 'The credentials it names are not deleted — only the fact that this pipeline uses them here.',
    confirmLabel: 'Remove',
    danger: true,
    onConfirm: async () => {
      setConfirm(null);
      setBusy('remove'); setError(null);
      try {
        await integrationsService.deleteImplementation(templateId, impl.id);
        navigate('/pipelines');
      } catch (e) {
        setError(apiErrorMessage(e, 'This pipeline could not be removed from the hotel.'));
        setBusy(null);
      }
    },
  });

  // "n of m" is computed from the bindings, so the header hears about every change on the board.
  const onSaved = useCallback((saved) => {
    setImpl(x => (x ? { ...x, filled: (saved.bindings || []).filter(b => b.credential_id).length } : x));
  }, []);
  const onBoardError = useCallback((msg) => setError(msg), []);

  // WHEEL BETWEEN THIS TEMPLATE'S HOTELS (sorted by name), wrapping around so you can keep cycling. Prev/Next buttons
  // and the ← / → arrow keys both move; changing implementationId re-runs load().
  const sibIdx = siblings.findIndex(s => String(s.id) === String(implementationId));
  const canWheel = siblings.length > 1 && sibIdx >= 0;
  const prevSib = canWheel ? siblings[(sibIdx - 1 + siblings.length) % siblings.length] : null;
  const nextSib = canWheel ? siblings[(sibIdx + 1) % siblings.length] : null;
  const goTo = useCallback((id) => {
    if (id == null) return;
    navigate(`/pipelines/${templateId}/implementations/${id}${tab !== 'pipeline' ? `?tab=${tab}` : ''}`);
  }, [navigate, templateId, tab]);

  useEffect(() => {
    const onKey = (e) => {
      if (!canWheel) return;
      const el = e.target;
      if ((el && /^(INPUT|SELECT|TEXTAREA)$/.test(el.tagName)) || (el && el.isContentEditable)) return;
      if (e.key === 'ArrowLeft') { e.preventDefault(); goTo(prevSib && prevSib.id); }
      else if (e.key === 'ArrowRight') { e.preventDefault(); goTo(nextSib && nextSib.id); }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [canWheel, prevSib, nextSib, goTo]);

  const back = (
    <Button variant="secondary" onClick={() => navigate('/pipelines')}><ArrowLeft className="icon" /> Back</Button>
  );

  if (notFound) {
    return (
      <div style={{ padding: '2rem 2.5rem' }}>
        {back}
        <p style={{ color: '#6b7280', marginTop: '1rem' }}>There is no such pipeline at a hotel you can see.</p>
      </div>
    );
  }
  if (!impl) {
    return (
      <div style={{ padding: '2rem 2.5rem' }}>
        {error ? <div className="os-msg os-msg-error">{error}</div> : <Spinner />}
      </div>
    );
  }

  // An adapter of this template whose system takes per-implementation code conversions — the Conversions tab's reason to exist.
  const hasConversions = !!(graph && (graph.nodes || []).some(n => n.role === 'SOURCE' && n.has_pipeline_conversions === 'Y'));

  return (
    <div style={{ padding: '2rem 2.5rem' }}>
      <PipelinesHeader
        subtitle={`${template ? template.name : 'A pipeline template'} at ${impl.hotelname || impl.name}. Set its credentials, move its version and read its logs.`}
        actions={back}
        tab="hotels"
      />

      <div style={{ marginBottom: '1rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
          {canWheel && (
            <button type="button" className="btn btn-secondary" onClick={() => goTo(prevSib && prevSib.id)}
              title={prevSib ? `Previous hotel: ${prevSib.hotelname} (←)` : ''} aria-label="Previous hotel"
              style={{ padding: '0.3rem 0.5rem', lineHeight: 0 }}>
              <ChevronLeft size={18} />
            </button>
          )}
          <h2 style={{ fontSize: '1.35rem', fontWeight: 700, color: '#111827', margin: 0 }}>{impl.hotelname || impl.name}</h2>
          {canWheel && (
            <>
              <button type="button" className="btn btn-secondary" onClick={() => goTo(nextSib && nextSib.id)}
                title={nextSib ? `Next hotel: ${nextSib.hotelname} (→)` : ''} aria-label="Next hotel"
                style={{ padding: '0.3rem 0.5rem', lineHeight: 0 }}>
                <ChevronRight size={18} />
              </button>
              <span style={{ color: '#6b7280', fontSize: '0.8rem' }}>{sibIdx + 1} of {siblings.length}</span>
            </>
          )}
        </div>
        {impl.chain_name && <p style={{ color: '#6b7280', margin: '0.3rem 0 0' }}>{impl.chain_name}</p>}
      </div>

      {/* WHAT IT RUNS: the template (its shape is changed there), the version (moved here), the status and how much of
          it is filled. */}
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-start', gap: '1.5rem', background: '#fff',
        border: '1px solid #e5e7eb', borderRadius: 12, padding: '1rem 1.25rem', marginBottom: '1rem' }}>
        <div>
          <div className="form-label" style={{ margin: '0 0 0.3rem' }}>Pipeline template</div>
          {/* Opened FROM this hotel's pipeline: the template's Back returns here, and the sidebar stays on Pipelines. */}
          <Link to={`/pipeline-templates/${templateId}/edit?from=implementation&returnTo=${encodeURIComponent(`/pipelines/${templateId}/implementations/${implementationId}`)}&tab=design`}
            style={{ fontWeight: 600, color: '#1e40af' }}>
            {template ? template.name : `#${templateId}`}
          </Link>
        </div>
        <div>
          <div className="form-label" style={{ margin: '0 0 0.3rem' }}>Version</div>
          <ImplementationVersionSelect
            impl={impl}
            versions={publishedVersions}
            liveVersionId={versions.liveVersionId}
            disabled={busy === 'version'}
            onChange={changeVersion}
          />
        </div>
        <div>
          <div className="form-label" style={{ margin: '0 0 0.3rem' }}>Status</div>
          <ImplementationStatusSelect impl={impl} disabled={busy === 'status'} onChange={changeStatus} />
        </div>
        <div>
          <div className="form-label" style={{ margin: '0 0 0.3rem' }}>Filled</div>
          <span className={impl.filled < impl.slots ? 'iw-empty' : ''}>{impl.filled} of {impl.slots}</span>
        </div>
        <div style={{ marginLeft: 'auto', alignSelf: 'center' }}>
          <button
            type="button"
            className="pp-criteria-add-btn pp-criteria-add-btn-danger"
            onClick={remove}
            disabled={busy === 'remove'}
          >
            <Trash2 size={14} /> {busy === 'remove' ? 'Removing…' : 'Remove'}
          </button>
        </div>
      </div>

      {error && <div className="os-msg os-msg-error" style={{ marginBottom: '1rem' }}>{error}</div>}

      <SubTabs
        tabs={[
          { key: 'pipeline', label: 'Pipeline' },
          // CONVERSIONS (V721) — only when an adapter of this template has them (marketplace.has_pipeline_conversions).
          ...(hasConversions ? [{ key: 'conversions', label: 'Conversions' }] : []),
          { key: 'logs', label: 'Logs' },
          { key: 'support', label: 'Support' },
        ]}
        active={tab}
        onChange={setTab}
      />

      <div style={{ marginTop: '1rem' }}>
        {tab === 'conversions' && hasConversions ? (
          <PipelineConversionsTab
            templateId={templateId}
            implementationId={impl.id}
            nodes={graph ? graph.nodes || [] : []}
            siblings={siblings}
            isSuperAdmin={isSuperAdmin}
          />
        ) : tab === 'support' ? (
          <ProductSupportTab product="Pipelines" productId={impl.id} noun="this pipeline"
            context={{ screen: 'pipeline-implementation', integrationId: Number(templateId), implementationId: impl.id,
              template: template ? template.name : undefined }} />
        ) : tab === 'logs' ? (
          <IntegrationLogsTab
            integrationId={templateId}
            hotelId={impl.hotel_id}
            scopeName={`${template ? template.name : 'this template'} at ${impl.hotelname || 'this hotel'}`}
          />
        ) : !graph ? <Spinner /> : (
          <ImplementationBoard
            templateId={templateId}
            implementationId={impl.id}
            graph={graph}
            status={impl.status}
            isSuperAdmin={isSuperAdmin}
            onSaved={onSaved}
            onError={onBoardError}
          />
        )}
      </div>

      {confirm && (
        <ConfirmModal
          title={confirm.title}
          message={confirm.message}
          confirmLabel={confirm.confirmLabel}
          danger={confirm.danger}
          onConfirm={confirm.onConfirm}
          onCancel={() => setConfirm(null)}
        />
      )}

    </div>
  );
}
